import { useState, useEffect } from 'react';
import { X, Copy, Check, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface ScootMessageModalProps {
  creator: any;
  onClose: () => void;
  onError?: (message: string) => void;
  onSuccess?: () => void;
}

export default function ScootMessageModal({ creator, onClose, onError, onSuccess }: ScootMessageModalProps) {
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [analyzedPosts, setAnalyzedPosts] = useState<any[]>([]);
  const [showPostsDebug, setShowPostsDebug] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);

  useEffect(() => {
    if (!hasGenerated && !generating) {
      generateMessage();
    }
  }, []);

  async function generateMessage() {
    if (generating || hasGenerated) return;

    setGenerating(true);
    setHasGenerated(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      // Load posts for context
      const { data: posts, error: postsError } = await supabase
        .from('posts')
        .select('caption, likes_count, comments_count, timestamp')
        .eq('influencer_id', creator.id)
        .order('timestamp', { ascending: false })
        .limit(10);

      console.log('Posts chargés pour', creator.username, '(ID:', creator.id, '):', posts?.length || 0, 'posts');
      if (postsError) console.error('Erreur chargement posts:', postsError);

      setAnalyzedPosts(posts || []);

      const { data: personaData } = await supabase
        .from('creator_persona')
        .select('persona_text, persona_json')
        .eq('influencer_id', creator.id)
        .maybeSingle();

      // Get agency info
      const { data: userData } = await supabase
        .from('users')
        .select('agency_id, first_name, last_name')
        .eq('id', authUser.id)
        .maybeSingle();

      let agencyName = 'Swipe-Upp';
      let managerName = 'Alexis';
      let agencyAddress = 'Paris, 11ème';

      if (userData?.agency_id) {
        const { data: agencyData } = await supabase
          .from('agencies')
          .select('name, address')
          .eq('id', userData.agency_id)
          .maybeSingle();

        if (agencyData) {
          agencyName = agencyData.name || agencyName;
          agencyAddress = agencyData.address || agencyAddress;
        }

        if (userData.first_name && userData.last_name) {
          managerName = `${userData.first_name} ${userData.last_name}`;
        }
      }

      // DEBUG: Log posts data
      console.log('🔍 DEBUG - Posts disponibles:', posts);
      console.log('🔍 DEBUG - Nombre de posts:', posts?.length);
      console.log('🔍 DEBUG - Premier post:', posts?.[0]);

      // Generate AI message via Edge Function
      console.log('🚀 Appel Edge Function en cours...');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/scoot-generate-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          creator,
          posts: posts || [],
          persona: personaData || null,
        }),
      });

      const data = await response.json();
      console.log('✅ Réponse Edge Function complète:', data);

      if (!response.ok) {
        console.error('❌ Erreur Edge Function:', data);
        if (response.status === 402) {
          // Special error for insufficient credits - no fallback
          setMessage('');
          if (onError) {
            onError(data.message || 'Crédits insuffisants pour générer ce message. Veuillez recharger votre compte.');
          }
          return;
        }
        throw new Error(`Edge Function error: ${data.error || 'Unknown error'}`);
      }

      const generatedMessage = data.message || '';
      console.log('📧 Message généré:', generatedMessage);
      console.log('📏 Longueur du message:', generatedMessage.length);

      if (!generatedMessage) {
        console.error('❌ Aucun message généré par l\'Edge Function');
        throw new Error('No message generated');
      }

      setMessage(generatedMessage);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('❌ ERREUR lors de la génération:', error);
      console.error('❌ Type d\'erreur:', error instanceof Error ? error.message : 'Unknown');

      // Fallback to template ONLY if API fails (but not for credit errors)
      console.warn('⚠️ Utilisation du message de fallback (l\'API a échoué)');
      const fallbackMessage = `Bonjour ${creator.display_name || creator.full_name || 'vous'},\n\nJ'esp\u00e8re que tu vas bien.\n\nJe suis Alexis, talent manager chez Swipe-Upp, agence de marketing d'influence \u00e0 Paris.\n\nJ'ai suivi ton travail avec beaucoup d'attention ces derni\u00e8res semaines, et je tenais \u00e0 te dire \u00e0 quel point j'appr\u00e9cie ton univers et la mani\u00e8re dont tu engages ta communaut\u00e9.\n\nJ'aimerais beaucoup \u00e9changer avec toi pour d\u00e9couvrir ta vision de l'influence, ton parcours et tes objectifs pour cette ann\u00e9e. Ce serait aussi l'occasion de te pr\u00e9senter notre agence et de pouvoir te mettre sur les castings avec les marques avec qui nous travaillons.\n\nSi cela te dit, je serais ravi de te rencontrer autour d'un caf\u00e9. Nos bureaux sont bas\u00e9s \u00e0 Paris dans le 11\u00e8me, alors n'h\u00e9site pas \u00e0 me faire part de tes disponibilit\u00e9s si tu es de passage ! Sinon, nous pouvons tout \u00e0 fait faire un visio selon ta convenance \u2600\ufe0f\n\nAussi, tu peux d'ores et d\u00e9j\u00e0 me transmettre ton kit m\u00e9dia si tu es d'accord.\n\nAu plaisir de te lire,\nBonne journ\u00e9e,\n\nAlexis`;
      setMessage(fallbackMessage);

      if (onError) {
        onError('Erreur lors de la génération du message. Un message de secours a été utilisé.');
      }
    } finally {
      setGenerating(false);
    }
  }

  const handleRegenerate = async () => {
    setHasGenerated(false);
    await generateMessage();
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <h3 className="text-xl font-bold text-white">
            Message d'approche pour {creator.display_name}
          </h3>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/70 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-160px)]">
          {generating ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/40 mb-4"></div>
              <p className="text-white/60">Génération du message personnalisé...</p>
              <p className="text-white/40 text-sm mt-2">Analyse des posts récents en cours</p>
            </div>
          ) : (
            <>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-white/50 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-white/40" />
                    Message généré par IA
                  </p>
                  <button
                    onClick={handleRegenerate}
                    disabled={generating}
                    className="text-sm text-white/50 hover:text-white/80 flex items-center gap-1 transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    Régénérer
                  </button>
                </div>
                {analyzedPosts.length > 0 ? (
                  <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-3">
                    <p className="text-xs text-white/60 font-medium mb-1">
                      {analyzedPosts.length} posts analysés pour personnaliser ce message
                    </p>
                    <button
                      onClick={() => setShowPostsDebug(!showPostsDebug)}
                      className="text-xs text-white/40 hover:text-white/60 underline transition-colors"
                    >
                      {showPostsDebug ? 'Masquer' : 'Voir les posts utilisés'}
                    </button>
                    {showPostsDebug && (
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {analyzedPosts.slice(0, 5).map((post, i) => (
                          <div key={i} className="text-xs bg-white/[0.04] p-2 rounded-lg border border-white/[0.06]">
                            <p className="text-white/60 font-medium">Post {i + 1}:</p>
                            <p className="text-white/40 truncate">{post.caption?.substring(0, 100) || 'Pas de description'}...</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-amber-500/[0.08] border border-amber-500/20 rounded-xl p-3">
                    <p className="text-xs text-amber-400">
                      Aucun post trouvé pour ce créateur. Message généré sans analyse de contenu.
                    </p>
                  </div>
                )}
              </div>
              <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full text-white/80 whitespace-pre-wrap leading-relaxed bg-transparent border-none focus:outline-none resize-none"
                  rows={15}
                />
              </div>
              <p className="text-white/30 text-xs mt-4">
                Le message est modifiable. Il a été généré en analysant les posts récents du créateur.
              </p>
            </>
          )}
        </div>

        <div className="p-6 border-t border-white/[0.08]">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 rounded-xl transition-colors"
            >
              Fermer
            </button>
            <button
              onClick={handleCopy}
              className="px-4 py-2 bg-white text-black hover:bg-white/90 rounded-xl flex items-center gap-2 transition-colors font-semibold"
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4" />
                  Copié !
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copier
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
